// BaseChart class serves as a base class for all chart types

// Define a common type for columns metadata
export type ColumnMetaData = {
  type: string;
  format?: string;
  unit?: string;
  description?: string;
};

export abstract class BaseProcessor {
  // Common properties and methods for all charts
  protected abstract chartType: string;
  // Data should be an array of objects with key-value pairs representing each data point
  protected data: { [key: string]: any }[] = [];
  protected options: any = {};
  protected xAxis: string[] = [];
  protected yAxis: string[] = [];
  protected groupBy: string[] = [];
  protected columnsMetaData: {
    [key: string]: ColumnMetaData;
  } = {};
  protected abstract preprocessData(): any;

  constructor() {
    this.addChartTypeToOptions();
  }

  /**
   * Parses a date string based on the provided format.
   * @param dateStr The date string to parse.
   * @param format The format of the date string (e.g., 'YYYY-MM-DD HH:mm:ss').
   * @returns The timestamp in milliseconds or null if parsing fails.
   */
  parseDate(dateStr: string, format: string | undefined): number | null {
    if (!dateStr || !format) return null;
    let fomratedDate = new Date(dateStr);
    if (!isNaN(fomratedDate.getTime())) {
      return fomratedDate.getTime();
    }

    // Supported tokens and their regex patterns
    const tokenPatterns: any = {
      YYYY: '(\\d{4})',
      MM: '(\\d{1,2})',
      DD: '(\\d{1,2})',
      HH: '(\\d{1,2})',
      mm: '(\\d{1,2})',
      SS: '(\\d{1,2})',
    };

    // Build regex pattern from format
    let regexPattern = format;
    Object.keys(tokenPatterns).forEach((token) => {
      regexPattern = regexPattern.replace(token, tokenPatterns[token]);
    });

    const regex = new RegExp('^' + regexPattern + '$');
    const match = dateStr.match(regex);
    if (!match) return null;

    // Extract values based on format order
    const formatOrder: any = [];
    let idx = 1;
    let tempFormat = format;
    Object.keys(tokenPatterns).forEach((token) => {
      const pos = tempFormat.indexOf(token);
      if (pos !== -1) {
        formatOrder.push({ token, idx, pos });
        tempFormat = tempFormat.replace(token, ' '.repeat(token.length));
        idx++;
      }
    });
    // Sort by position in format string
    formatOrder.sort((a: any, b: any) => a.pos - b.pos);

    // Default values
    let year = 1970,
      month = 1,
      day = 1,
      hour = 0,
      minute = 0,
      second = 0;
    formatOrder.forEach((item: any, i: any) => {
      const value = parseInt(match[i + 1], 10);
      switch (item.token) {
        case 'YYYY':
          year = value;
          break;
        case 'MM':
          month = value;
          break;
        case 'DD':
          day = value;
          break;
        case 'HH':
          hour = value;
          break;
        case 'mm':
          minute = value;
          break;
        case 'SS':
          second = value;
          break;
      }
    });

    // JS Date: month is 0-based
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }

  formatDate(date: Date, format: string): string {
    const pad = (num: number) => num.toString().padStart(2, '0');

    const replacements: { [key: string]: string } = {
      YYYY: date.getFullYear().toString(),
      MM: pad(date.getMonth() + 1),
      DD: pad(date.getDate()),
      hh: pad(date.getHours()),
      mm: pad(date.getMinutes()),
      ss: pad(date.getSeconds()),
    };

    return format.replace(
      /YYYY|MM|DD|hh|mm|ss/g,
      (token) => replacements[token]
    );
  }

  formateValueWithType(value: any, valueMetadata: ColumnMetaData) {
    if (valueMetadata?.type === 'int' || valueMetadata?.type === 'float') {
      return parseFloat(parseFloat(value).toFixed(2));
    } else if (
      valueMetadata?.type === 'datetime' ||
      valueMetadata?.type === 'date' ||
      valueMetadata?.format
    ) {
      return this.parseDate(value, valueMetadata.format);
    } else if (valueMetadata?.type === 'string' && [null, undefined].includes(value)) {
      return '';
    } else {
      return value;
    }
  }

  protected getToolTipDateFormat(type: string) {
    if (type === 'month') {
      return '%b %Y';
    } else if (type === 'year') {
      return '%Y';
    }
    return '%Y-%m-%d';
  }

  protected isDateTime(datatype: string): boolean {
    return ['datetime', 'date', 'month', 'year'].includes(datatype);
  }

  protected addChartTypeToOptions() {
    this.options.chart = {
      type: this.chartType,
    };
  }

  protected addXAxisTitle() {
    if (this.xAxis.length > 0) {
      this.options.xAxis = {
        title: {
          text: this.xAxis.join(' & '),
        },
      };
    }
  }

  protected addYAxisTitle() {
    if (this.yAxis.length > 0) {
      this.options.yAxis = {
        title: {
          text: this.yAxis.join(' & '),
        },
      };
    }
  }

  getChartOption() {
    this.preprocessData();
    this.options.lang = {
      thousandsSep: ',',
    };
    this.options.credits = {
      enabled: false,
    };
    return this.options;
  }
}
